// src/chat.js
// Lógica central do chat — salva e lê tudo do Firestore

import { PHILOSOPHERS, TEACHER } from './data/philosophers.js'
import {
  getOrCreateInstance, addMessageToInstance, markDebateStarted,
  getOrCreateDM, addMessageToDM, listenToInstance, listenToDM,
  listenToGroup,
} from './db.js'
import {
  generateOpeningReaction, generateGroupReply,
  generateSpontaneousComment, generateDMReply, generateDMGreeting,
  splitResponse,
} from './ai.js'

function makeMessage(sender, text) { return { sender, text, ts: Date.now() } }
function delay(ms) { return new Promise((r) => setTimeout(r, ms)) }
function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
function buildHistory(messages) {
  return messages.slice(-10).map((m) => ({
    role: m.sender === 'Você' ? 'user' : 'assistant',
    content: m.sender === 'Você' ? m.text : `${m.sender}: ${m.text}`,
  }))
}

// ═══ GRUPO ═══════════════════════════════════════════════════════════════════

export async function openGroupInstance({ groupId, userId, group, onMessage, onTyping }) {
  const instance = await getOrCreateInstance(groupId, userId)
  let kickoffStarted = instance.debateStarted

  // Listener em tempo real — atualiza UI quando Firestore muda
  const unsubInstance = listenToInstance(groupId, userId, (data) => {
    onMessage(data.messages || [])
  })

  // Escuta mudança no grupo (ex: mensagem da Prof. chegando)
  const unsubGroup = listenToGroup(groupId, async (updatedGroup) => {
    if (updatedGroup.teacherMessage && !kickoffStarted) {
      kickoffStarted = true
      await _kickoffDebate({ groupId, userId, group: updatedGroup, onTyping })
    }
  })

  // Se já tem mensagem da prof e debate ainda não começou → inicia agora
  if (group.teacherMessage && !kickoffStarted) {
    kickoffStarted = true
    await _kickoffDebate({ groupId, userId, group, onTyping })
  }

  return {
    messages: [],
    unsubscribe: () => { unsubInstance(); unsubGroup() },
  }
}

async function _kickoffDebate({ groupId, userId, group, onTyping }) {
  await markDebateStarted(groupId, userId)

  // Primeira mensagem do debate é sempre a da professora
  const teacherMsg = makeMessage(TEACHER.name, group.teacherMessage)
  await addMessageToInstance(groupId, userId, teacherMsg, [])

  // Filósofos reagem em background sem bloquear
  _startDebate({ groupId, userId, group, onTyping, initialMessages: [teacherMsg] })
}

async function _startDebate({ groupId, userId, group, onTyping, initialMessages = [] }) {
  const philIds = shuffle([...group.philIds])
  const localMessages = [...initialMessages]

  for (let i = 0; i < philIds.length; i++) {
    const phil = PHILOSOPHERS[philIds[i]]
    if (!phil) continue

    await delay(900 + i * 700)
    onTyping(phil.name, phil.emoji, phil.color, true)
    await delay(1200 + Math.random() * 900)

    try {
      const text = await generateOpeningReaction(
        phil.name, phil.system(group.topic), group.topic, group.teacherMessage || ''
      )
      onTyping(phil.name, phil.emoji, phil.color, false)
      const msg = makeMessage(phil.name, text)
      localMessages.push(msg)
      await addMessageToInstance(groupId, userId, msg, buildHistory(localMessages))
    } catch (e) {
      onTyping(phil.name, phil.emoji, phil.color, false)
      console.error('Debate error:', e)
    }
  }
}

export async function sendGroupMessage({ groupId, userId, group, text, currentMessages = [], onTyping, onError }) {
  const localMessages = [...currentMessages]
  const userMsg = makeMessage('Você', text)
  localMessages.push(userMsg)
  const history = buildHistory(localMessages)
  await addMessageToInstance(groupId, userId, userMsg, history)

  const respondents = shuffle([...group.philIds]).slice(0, Math.min(group.philIds.length, 1 + Math.floor(Math.random() * 2)))
  let lastReply = ''

  for (let i = 0; i < respondents.length; i++) {
    const phil = PHILOSOPHERS[respondents[i]]
    if (!phil) continue

    await delay(500 + i * 400)
    onTyping(phil.name, phil.emoji, phil.color, true)
    await delay(1100 + Math.random() * 800)

    try {
      const replyText = await generateGroupReply(phil.name, phil.system(group.topic), text, history)
      onTyping(phil.name, phil.emoji, phil.color, false)
      const parts = splitResponse(replyText, phil.name)
      for (let p = 0; p < parts.length; p++) {
        if (p > 0) {
          onTyping(phil.name, phil.emoji, phil.color, true)
          await delay(1000 + Math.random() * 800)
          onTyping(phil.name, phil.emoji, phil.color, false)
        }
        const msg = makeMessage(phil.name, parts[p])
        lastReply = `${phil.name} disse: "${parts[p]}"`
        localMessages.push(msg)
        await addMessageToInstance(groupId, userId, msg, buildHistory(localMessages))
      }
    } catch (e) {
      onTyping(phil.name, phil.emoji, phil.color, false)
      console.error('Reply error:', e)
      if (onError) onError(e)
    }
  }

  // Comentário espontâneo 40% chance
  const others = group.philIds.filter((id) => !respondents.includes(id))
  if (others.length > 0 && Math.random() > 0.6 && lastReply) {
    const ep = PHILOSOPHERS[others[Math.floor(Math.random() * others.length)]]
    if (!ep) return
    await delay(1800 + Math.random() * 1200)
    onTyping(ep.name, ep.emoji, ep.color, true)
    await delay(900 + Math.random() * 700)
    try {
      const replyText = await generateSpontaneousComment(ep.name, ep.system(group.topic), `aluno disse "${text}", ${lastReply}`)
      onTyping(ep.name, ep.emoji, ep.color, false)
      const msg = makeMessage(ep.name, replyText)
      localMessages.push(msg)
      await addMessageToInstance(groupId, userId, msg, buildHistory(localMessages))
    } catch (e) {
      onTyping(ep.name, ep.emoji, ep.color, false)
    }
  }
}

// ═══ DM ══════════════════════════════════════════════════════════════════════

export async function openDM({ userId, philId, onMessage }) {
  await getOrCreateDM(userId, philId)
  const unsubscribe = listenToDM(userId, philId, (data) => onMessage(data.messages || []))
  return { messages: [], unsubscribe }
}

export async function startDMGreeting({ userId, philId, onTyping }) {
  const phil = PHILOSOPHERS[philId]
  if (!phil) return
  onTyping(phil.name, phil.emoji, phil.color, true)
  await delay(1200)
  try {
    const text = await generateDMGreeting(phil.name, phil.dm('filosofia geral'))
    onTyping(phil.name, phil.emoji, phil.color, false)
    await addMessageToDM(userId, philId, makeMessage(phil.name, text), [{ role: 'assistant', content: text }])
  } catch (e) {
    onTyping(phil.name, phil.emoji, phil.color, false)
  }
}

export async function sendDMMessage({ userId, philId, text, currentMessages = [], onTyping, onError }) {
  const phil = PHILOSOPHERS[philId]
  if (!phil) return
  const localMessages = [...currentMessages]
  const userMsg = makeMessage('Você', text)
  localMessages.push(userMsg)
  const history = buildHistory(localMessages)
  await addMessageToDM(userId, philId, userMsg, history)

  onTyping(phil.name, phil.emoji, phil.color, true)
  await delay(900 + Math.random() * 900)
  try {
    const replyText = await generateDMReply(phil.name, phil.dm('filosofia geral'), history)
    onTyping(phil.name, phil.emoji, phil.color, false)
    const parts = splitResponse(replyText)
    for (let p = 0; p < parts.length; p++) {
      if (p > 0) {
        onTyping(phil.name, phil.emoji, phil.color, true)
        await delay(1000 + Math.random() * 800)
        onTyping(phil.name, phil.emoji, phil.color, false)
      }
      const msg = makeMessage(phil.name, parts[p])
      localMessages.push(msg)
      await addMessageToDM(userId, philId, msg, buildHistory(localMessages))
    }
  } catch (e) {
    onTyping(phil.name, phil.emoji, phil.color, false)
    console.error('DM reply error:', e)
    if (onError) onError(e)
  }
}